import React from "react";
import { AbsoluteFill, Sequence, staticFile } from "remotion";
import { Audio } from "@remotion/media";
import { TransitionSeries, linearTiming } from "@remotion/transitions";
import { fade } from "@remotion/transitions/fade";
import { slide } from "@remotion/transitions/slide";
import { VIDEO } from "./theme";
import { S1Intro } from "./scenes/S1Intro";
import { S2Problem } from "./scenes/S2Problem";
import { S3Tracking } from "./scenes/S3Tracking";
import { SPuerto } from "./scenes/SPuerto";
import { SPDI } from "./scenes/SPDI";
import { SBodega } from "./scenes/SBodega";
import { SMapa } from "./scenes/SMapa";
import { S4Process } from "./scenes/S4Process";
import { STrazabilidad } from "./scenes/STrazabilidad";
import { SEntrega } from "./scenes/SEntrega";
import { S5Evidence } from "./scenes/S5Evidence";
import { S6Platforms } from "./scenes/S6Platforms";
import { S7Value } from "./scenes/S7Value";
import { S8CTA } from "./scenes/S8CTA";

const fps = VIDEO.fps;
const T = 18; // frames de transición (solape entre escenas)

// Duración por escena, dimensionada para que la narración entre con holgura
// (voz medida + ~0.5 s de respiro; ver src/narration.ts).
export const SCENES = [
  { id: "S1Intro", comp: S1Intro, dur: 4.8 },
  { id: "S2Problem", comp: S2Problem, dur: 4.3 },
  { id: "S3Tracking", comp: S3Tracking, dur: 5.1 },
  { id: "SPuerto", comp: SPuerto, dur: 7.2 },
  { id: "SPDI", comp: SPDI, dur: 8.1 },
  { id: "SBodega", comp: SBodega, dur: 6.4 },
  { id: "SMapa", comp: SMapa, dur: 6.8 },
  { id: "S4Process", comp: S4Process, dur: 8.5 },
  { id: "S5Evidence", comp: S5Evidence, dur: 7.9 },
  { id: "S6Platforms", comp: S6Platforms, dur: 5.3 },
  { id: "STrazabilidad", comp: STrazabilidad, dur: 7.4 },
  { id: "SEntrega", comp: SEntrega, dur: 7.7 },
  { id: "S7Value", comp: S7Value, dur: 6.4 },
  { id: "S8CTA", comp: S8CTA, dur: 6.4 },
] as const;

const frames = (s: number) => Math.round(s * fps);

export const TOTAL_FRAMES =
  SCENES.reduce((acc, s) => acc + frames(s.dur), 0) - (SCENES.length - 1) * T;

// Frame global en el que arranca cada escena (cada transición solapa T frames).
export const SCENE_STARTS: number[] = (() => {
  const out: number[] = [];
  let acc = 0;
  SCENES.forEach((s, i) => {
    out.push(acc);
    acc += frames(s.dur) - (i < SCENES.length - 1 ? T : 0);
  });
  return out;
})();

// Capa de voz en off: una pista por escena, al inicio de cada una.
const NarrationTrack: React.FC = () => {
  return (
    <>
      {SCENES.map((s, i) => (
        <Sequence key={s.id} from={SCENE_STARTS[i] + 6} name={`vo-${s.id}`}>
          <Audio src={staticFile(`voiceover/${s.id}.mp3`)} volume={1} />
        </Sequence>
      ))}
    </>
  );
};

type Props = { readonly narration?: boolean };

export const Video: React.FC<Props> = ({ narration = false }) => {
  return (
    <AbsoluteFill>
      <TransitionSeries>
        {SCENES.map((s, i) => {
          const Comp = s.comp;
          const trans =
            i % 2 === 0
              ? fade()
              : slide({ direction: i % 4 === 1 ? "from-right" : "from-left" });
          return (
            <React.Fragment key={s.id}>
              <TransitionSeries.Sequence durationInFrames={frames(s.dur)} name={s.id}>
                <Comp />
              </TransitionSeries.Sequence>
              {i < SCENES.length - 1 && (
                <TransitionSeries.Transition
                  presentation={trans}
                  timing={linearTiming({ durationInFrames: T })}
                />
              )}
            </React.Fragment>
          );
        })}
      </TransitionSeries>
      {narration && <NarrationTrack />}
    </AbsoluteFill>
  );
};
